import { FormikHelpers, FormikErrors } from 'formik';
import { captureException } from '@sentry/react';

interface ApiError {
  response?: {
    data?: Record<string, unknown>;
  };
  request?: unknown;
}

export const extractError = (errors: unknown): unknown => {
  if (Array.isArray(errors)) {
    return errors[0];
  }

  return errors;
};

export const submitHandler = async <T>(
  values: T,
  helpers: FormikHelpers<T>,
  onSend: () => unknown,
  onSuccess: (values: T, helpers: FormikHelpers<T>, result?: unknown) => unknown,
  onError?: (values: T, helpers: FormikHelpers<T>, error: unknown) => unknown
): Promise<unknown> => {
  const promise = onSend();

  // sync api call
  if (!(promise instanceof Promise)) {
    helpers.setSubmitting(false);
    return onSuccess(promise as T, helpers);
  }

  // async api call
  try {
    const result = await promise;
    helpers.setSubmitting(false);

    return onSuccess(values, helpers, result);
  } catch (err) {
    const error = err as ApiError;

    if (error.response) {
      const keys = Object.keys(error.response?.data || {});

      const errors = keys.reduce<Record<string, unknown>>((prev, next) => ({
        ...prev,
        [next]: extractError((error.response?.data ?? {})[next])
      }), {});

      // DRF battery don't support remmaping fields
      if (typeof errors.detail !== 'undefined') {
        errors.nonFieldErrors = errors.detail;
        delete errors.detail;
      }

      helpers.setErrors(errors as FormikErrors<T>);
    } else if (error.request) {
      captureException(error, { extra: { context: 'no_response' } });
    } else {
      captureException(error, { extra: { context: 'unexpected' } });
    }

    helpers.setSubmitting(false);

    return onError?.(values, helpers, err);
  }
};
